import NanoToast, { ToastType } from "../components/NanoToast";
import Bugsnag from "@bugsnag/js";
import {
  ApiError,
  ForbiddenError,
  NotFoundError,
  ServerError,
  asError,
} from "../Error/ApiErrors";

/**
 * A utility class with a collection of methods surrounding REST API calls
 * and the functions we commonly use to deal with those.
 */
class RestPromise {
  /**
   * This is a sort of factory method for an Ajax request
   * @param url
   * @param method
   * @param content
   * @param notifyActivity
   * @returns Promise
   */
  // Callers name the shape they expect on the .then, which only type-checks
  // against a permissive default; `unknown` here would ask every call site in
  // the app to cast instead.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static async Create<T = any>(
    url: string,
    method: "GET" | "PUT" | "POST" | "DELETE",
    content: object = null,
    notifyActivity = true,
  ): Promise<T> {
    if (notifyActivity) {
      $(window).trigger("user-activity");
    }

    const options: JQueryAjaxSettings = {
      type: method,
      url: url,
      contentType: "application/json",
      dataType: "json",
    };
    if (content !== null) {
      options.data = JSON.stringify(content);
    }

    try {
      return (await $.ajax(options)) as T;
    } catch (error) {
      return this.handleApiError<T>(error, url, method);
    }
  }

  public static DisplayRestSuccess(message: string): void {
    const successToast = new NanoToast({
      parentContainer: "body",
      pos: "nanotoast-top-center",
      type: "success",
      text: message,
      asideIcon: "far fa-thumbs-up",
      duration: 300,
      hideDelay: 5000,
    });
    successToast.Show();
  }

  public static DisplayRestError(error: ApiError): void {
    // A programming error reaching here carries no message for anyone to read.
    // It goes to the reporter with the rest of the bugs, and the student still
    // hears that something failed rather than watching a button do nothing.
    if (error?.userMessage === undefined) {
      const reported = asError(error);
      Bugsnag.notify(reported);
      new NanoToast({
        parentContainer: "body",
        pos: "nanotoast-top-center",
        type: "error",
        text: reported.message,
        asideIcon: "fas fa-bug",
        duration: 300,
        hideDelay: 5000,
        zIndex: 350,
      }).Show();
      return;
    }
    if (+error.status !== 500 && +error.status !== 0) {
      const errorToast = new NanoToast({
        parentContainer: "body",
        pos: "nanotoast-top-center",
        type: "error",
        text: error.userMessage,
        asideIcon: "fas fa-bug",
        duration: 300,
        hideDelay: 5000,
        zIndex: 350,
      });
      errorToast.Show();
    } else {
      Bugsnag.notify(error);
    }
  }

  public static DisplayRestMessage(
    message: string,
    className: ToastType,
    icon = "fa-bug",
  ) {
    const errorToast = new NanoToast({
      parentContainer: "body",
      pos: "nanotoast-top-center",
      type: className,
      text: message,
      asideIcon: `fas ${icon}`,
      duration: 300,
      hideDelay: 5000,
      zIndex: 350,
    });
    errorToast.Show();
  }

  public static DisplayRestWarning(message: string) {
    const errorToast = new NanoToast({
      parentContainer: "body",
      pos: "nanotoast-top-center",
      type: "warning",
      text: message,
      asideIcon: "fas fa-exclamation",
      duration: 300,
      hideDelay: 5000,
      zIndex: 350,
    });
    errorToast.Show();
  }

  /**
   * Ping the server, returns true if the server answer a 200 response
   * @constructor
   */
  public static RequestServerHealth(): Promise<boolean> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", "/health", true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.onload = () => {
        resolve(xhr.status === 200);
      };
      xhr.timeout = 2000;
      xhr.ontimeout = () => resolve(false);
      xhr.onerror = () => resolve(false);
      xhr.send();
    });
  }

  /**
   * Handles API errors from jQuery AJAX calls and converts them to typed errors
   * @param error The error caught from jQuery ajax
   * @param url The URL that was being requested
   * @param method The HTTP method used
   * @returns Promise that either resolves (for redirects) or rejects with a typed error
   */
  private static handleApiError<T>(
    error: unknown,
    url?: string,
    method?: string,
  ): Promise<T> {
    // Define type for jQuery ajax errors
    interface AjaxError {
      status: number;
      responseText: string;
      responseJSON?: {
        message?: string;
        detail?: string;
      };
    }

    // Cast to our interface
    const ajaxError = error as AjaxError;

    // The JSON body, when there is one: what the typed error carries as responseData, and the
    // only source of a message the server actually wrote (ApiError.serverMessage)
    let body = ajaxError.responseJSON;

    // Extract error message
    let errorMessage = "Unknown error occurred";

    if (body?.message) {
      errorMessage = body.message;
    } else if (body?.detail) {
      errorMessage = body.detail;
    } else if (ajaxError.responseText) {
      try {
        // Try to parse responseText as JSON
        const parsedError = JSON.parse(ajaxError.responseText) as {
          message?: string;
          detail?: string;
        };
        if (typeof parsedError === "object" && parsedError !== null) {
          body = parsedError;
        }
        errorMessage =
          parsedError.message ?? parsedError.detail ?? ajaxError.responseText;
      } catch {
        // If parsing fails, use responseText directly
        errorMessage = ajaxError.responseText;
      }
    }

    // Handle errors based on status code
    switch (ajaxError.status) {
      case 401: {
        // The load balancer answers a support REST call whose Google sign-in
        // session has lapsed with a bare 401; a page load renews the session,
        // so the user retries the action rather than losing their app login.
        if (url?.startsWith("/support/rest")) {
          window.location.reload();
          return;
        }
        if (errorMessage.includes("login_token_invalid")) {
          window.location.href = "/logout?logout-reason=second-login";
          return;
        } else {
          window.location.href = "/logout?logout-reason=logged-out";
          return;
        }
      }

      case 403:
        throw new ForbiddenError(
          errorMessage,
          body,
          url,
          method,
        );

      case 404:
        throw new NotFoundError(
          errorMessage,
          body,
          url,
          method,
        );

      case 500:
        throw new ServerError(
          errorMessage,
          body,
          url,
          method,
        );

      default:
        // For all other errors, throw a generic ApiError
        throw new ApiError(
          errorMessage,
          ajaxError.status || 0,
          body,
          url,
          method,
        );
    }
  }
}

export default RestPromise;
